import { type OnboardingDraftPhoneNumberInput } from 'CDK_API';
import { type OnboardingProduct } from 'app/services/paidol.service';

import { faker } from '@faker-js/faker';
import { matchIsValidTel } from 'mui-tel-input';
import dwollaBusinessIndustryClassifications from 'util/dwolla_business_industry_classifications.json';
import * as yup from 'yup';
import translateOrKey from 'util/translateOrKey';

/**
 * The CDK `listProducts` returns `type` as a plain string, so there is no generated enum to compare
 * against. `HighnoteProductType` came from the Amplify API and disappears with the teardown.
 */
export const HIGHNOTE_PRODUCT_TYPE_CREDIT = 'CREDIT';

/**
 * SEE-1311: the Amplify teardown removed the auto-gen `BeneficialOwnerInput`, whose required
 * fields were the type-level contract the owner form relied on. Its CDK replacement,
 * `OnboardingDraftBeneficialOwnerInput`, is all-optional-and-nullable because a draft may be
 * partial — correct at the wire boundary, useless for form logic that dereferences every field.
 *
 * This is the one strict shape for a fully materialized owner, shared by the form component and
 * `generateBeneficialOwnerSchema` (the yup schema enforces exactly these required fields at
 * runtime). It is intentionally assignable to `OnboardingDraftBeneficialOwnerInput`, so completed
 * owners flow into the draft-save call without casts. Do not re-declare a local variant in a
 * component — two shapes under one name is how the optionality guarantees silently rot.
 */
export interface CompleteBeneficialOwner {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  suffix?: string | null;
  email: string;
  phoneNumber: OnboardingDraftPhoneNumberInput;
  phoneNumberValue: string;
  phoneExtension?: string | null;
  hasExtension?: boolean | null;
  homeAddress: {
    address: string;
    extendedAddress?: string | null;
    zip: string;
    city: string;
    state: string;
    country?: string | null;
  };
  socialSecurityNumber: string;
  birthdate: string;
  percentageOwnership: number;
}

/**
 * SEE-1274 (task 3.7). The server returns saved SSNs and EINs masked with U+2022 — `•••-••-1234` and
 * `••-•••1234` — and treats a submitted value containing that character as "leave this field alone"
 * (`onboarding_session_common.py:343`). The full value never reaches the browser.
 *
 * Two consequences the forms have to honour:
 *  - a digit-only input mask strips the bullets, so a masked value must bypass the mask to display;
 *  - format validation can never pass against a mask, so it has to be skipped rather than "fixed".
 */
export const SENSITIVE_MASK_CHAR = '\u2022';

export const isMaskedSensitiveValue = (value: unknown): boolean =>
  typeof value === 'string' && value.includes(SENSITIVE_MASK_CHAR);

/**
 * SEE-1274 F3. A mask is a token meaning "keep the stored value", so a previously-saved sensitive
 * field has exactly two acceptable states: the mask **byte-for-byte** as the server rendered it, or a
 * complete new value. Anything between the two — typing over some of the bullets, editing the visible
 * digits — is rejected server-side with a 400.
 *
 * The field looks editable and partly is, which makes this the likeliest way for a real applicant to
 * get stuck, so it needs a message of its own rather than the format error.
 *
 * Compared by exact equality against the masks the server actually sent, never against a hardcoded
 * shape: the mask is generated server-side and its exact character count is what the server's own
 * equality check compares. That is also why it took `birthdate` and `phoneNumber` unchanged when cdk
 * `dbe40bd2` started masking them — only the list of stored values grew.
 */
export const isPartiallyEditedMask = (value: unknown, storedMasks: readonly string[] = []): boolean =>
  isMaskedSensitiveValue(value) && !storedMasks.includes(value as string);

/** Only masks are worth comparing against; a cleared or never-saved field has nothing to preserve. */
export const collectStoredMasks = (...values: unknown[]): string[] =>
  values.filter((value): value is string => isMaskedSensitiveValue(value));

export const PARTIAL_MASK_MESSAGE =
  'Leave the saved value as it is, or clear it and type a complete new one.';

export const determineEnvironment = (): string => {
  if (
    process.env.REACT_APP_FEATURE_ENVIRONMENT && // Set in Amplify build settings
    process.env.NODE_ENV !== 'test' // CI running on deploys
  ) {
    return process.env.REACT_APP_FEATURE_ENVIRONMENT;
  }

  // Used in local development
  if (['production', 'development', 'test'].includes(process.env.NODE_ENV)) {
    return process.env.NODE_ENV;
  }

  // Always default to prod
  return 'production';
};

export const getIndustryClassificationsOptions = () => {
  return dwollaBusinessIndustryClassifications._embedded['business-classifications']
    .flatMap((businessClassification) =>
      businessClassification._embedded['industry-classifications'].map((industryClassification) => ({
        industryId: industryClassification.id,
        industryName: industryClassification.name,
        businessId: businessClassification.id,
        businessName: businessClassification.name,
      }))
    )
    .sort((a, b) => {
      const businessComparison = a.businessName.localeCompare(b.businessName);
      if (businessComparison !== 0) {
        return businessComparison;
      }
      return a.industryName.localeCompare(b.industryName);
    });
};

export const getInitialOrganizationProfile = () => {
  return {
    businessType: '',
    billingAddress: {
      state: '',
      extendedAddress: '',
    },
    annualRevenue: '',
  };
};

export const getInitialPrimaryAuthorizedPerson = () => {
  return {
    homeAddress: {
      state: '',
      extendedAddress: '',
    },
  };
};

export const getInitialAdministrator = () => {
  return {
    firstName: '',
    lastName: '',
    email: '',
  };
};

/**
 * SEE-1388: Highnote's validation patterns, copied verbatim from its API schema so the form refuses
 * exactly what Highnote refuses — no looser (rejected at final submit) and no stricter (a real
 * applicant blocked for nothing). Re-check them against the schema before changing either side.
 */
export const HIGHNOTE_STREET_ADDRESS_WORDS = /^\s*\S+(?:\s+\S+){1,3}/;
export const HIGHNOTE_STREET_ADDRESS_APPLICATION =
  /^(?:((\S([^pPOo])+)|(?:[0-9]+)))\s(?:[0-9A-Za-z.]|[^\S\r\n])+$/;
export const HIGHNOTE_LOCALITY = /^['\p{L}]+(?:[ \p{L},'-:])*$/u;
const STREET_ADDRESS_MESSAGE = 'Enter a street number and street name, for example 123 Main St';

const DEFAULT_PO_BOX_MESSAGE = 'PO Box is not allowed in street address. Please use the second address line.';

export const generateAddressSchema = (poBoxMessage = DEFAULT_PO_BOX_MESSAGE) => {
  return yup.object().shape({
    country: yup.string().required('Country is required'),
    address: yup
      .string()
      .required('Address is required')
      .test('no-po-box', poBoxMessage, (value) => {
        if (!value) return true;
        return !/p\.?\s*o\.?\s*box/i.test(value);
      })
      // SEE-1388: Highnote's own two checks for an application's street address, verbatim from its
      // API schema (`AddressInput.streetAddress`). Two to four words, and after the first word only
      // letters, digits, periods and spaces (its PO Box guard). Anything looser was refused at final
      // submit, after the applicant had moved on.
      .matches(HIGHNOTE_STREET_ADDRESS_WORDS, STREET_ADDRESS_MESSAGE)
      .matches(HIGHNOTE_STREET_ADDRESS_APPLICATION, STREET_ADDRESS_MESSAGE),
    extendedAddress: yup
      .string()
      .test('no-po-box', poBoxMessage, (value) => {
        if (!value) return true;
        return !/p\.?\s*o\.?\s*box/i.test(value);
      })
      .test('valid-extented-address', 'Invalid extended address', (value) => {
        if (!value) return true;
        return /^[a-zA-Z\d',. \-#]+(([',. \-#][a-zA-Z ])?[a-zA-Z.]*)*$/.test(value);
      }),
    zip: yup
      .string()
      .required('ZIP code is required')
      .matches(/^\d{5}(-\d{4})?$/, 'Invalid ZIP code'),
    city: yup
      .string()
      .required('City is required')
      // SEE-1388: Highnote's `AddressInput.locality` pattern, verbatim. No length cap and accented
      // letters allowed; the old 25-character, ASCII-only rule refused real city names.
      .matches(HIGHNOTE_LOCALITY, 'Invalid city'),
    state: yup.string().required('State is required'),
  });
};

export const generatePhoneNumberSchema = (storedMasks: readonly string[] = []) => {
  return yup.object().shape({
    countryCode: yup.string().required('Country code is required'),
    // The server masks the nested `number` as well as the flat `phoneNumberValue`, and both have to
    // round-trip. A mask satisfies `.required()` on its own; this only guards against editing one.
    number: yup
      .string()
      .required('Phone number is required')
      .test('mask-untouched', PARTIAL_MASK_MESSAGE, (value) => !isPartiallyEditedMask(value, storedMasks)),
    extension: yup.string().nullable(),
  });
};

/**
 * A saved birth date arrives as `••/••/1985`, which is not a date and never will be. `yup.date()`
 * casts it to null and then `.required()` reports it missing, so the mask needs its own branch rather
 * than a test bolted onto the date schema.
 *
 * `yup.lazy` picks the branch per value, which keeps every age and range rule intact the moment the
 * applicant clears the mask and enters a real date.
 */
const generateBirthdateSchema = (storedMasks: readonly string[] = []) =>
  yup.lazy((value: unknown) =>
    isMaskedSensitiveValue(value)
      ? yup
          .string()
          .test(
            'mask-untouched',
            PARTIAL_MASK_MESSAGE,
            (masked) => !isPartiallyEditedMask(masked, storedMasks)
          )
      : yup
          .date()
          .test('age', 'Please enter a valid birthdate. Age must be between 18 and 99 years', (dateValue) => {
            const today = new Date();
            // Let `.required()` own emptiness so a blank field reads "Birthdate is required" rather
            // than the age message — yup reports this test first, and the age wording is confusing
            // on a field the user simply hasn't filled in yet (SPE-3659).
            if (!dateValue) return true;
            if (dateValue > today) return false;
            const yearDiff = today.getFullYear() - dateValue.getFullYear();
            const monthDiff = today.getMonth() - dateValue.getMonth();
            const dayDiff = today.getDate() - dateValue.getDate();
            const age = yearDiff - (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? 1 : 0);
            // Highnote's schema says "Maximum age: 100 years" but refuses a 100-year-old
            // (ASSOCIATED_PERSON_OVER_MAX_AGE, seen 2026-10-15), so the limit is under 100.
            return age >= 18 && age <= 99;
          })
          .required('Birthdate is required')
          .nullable()
          .transform((v) => (v instanceof Date && !isNaN(v as unknown as number) ? v : null))
  );

export const generateOrganizationProfileSchema = (
  poBoxMessage = DEFAULT_PO_BOX_MESSAGE,
  storedMasks: readonly string[] = []
) => {
  return yup.object().shape({
    productId: yup.string().required('Select one product'),
    legalBusinessName: yup.string().required('Legal business name is required'),
    doingBusinessAs: yup.string().required('Doing business as name is required'),
    phoneNumber: generatePhoneNumberSchema(storedMasks),
    phoneNumberValue: generatePhoneNumberValueSchema(storedMasks),
    hasExtension: yup.boolean().nullable(),
    phoneExtension: yup
      .string()
      .when('hasExtension', {
        is: true,
        then: yup.string().required('You must enter an extension'),
      })
      .nullable(),
    businessType: yup.string().required('Business type is required'),
    annualRevenue: yup
      .string()
      // SEE-1388: Highnote accepts any non-negative amount (a $50M submit was accepted), so only a
      // zero is caught here: for a card application it is a typo or a placeholder, never real.
      .test('min-revenue', 'Annual revenue must be at least $1', (value) => {
        if (!value) return true; // blank is `required`'s call, and only for CREDIT
        const amount = Number(value.replace(/[^\d.]/g, ''));
        return Number.isFinite(amount) && amount >= 1;
      })
      .when(['productId', '$products'], {
        is: (productId: string, products: OnboardingProduct[]) => {
          const selectedProduct = products.find((product) => product.id === productId);
          /**
           * A product we cannot resolve counts as "might be CREDIT", not "is not CREDIT".
           *
           * `selectedProduct` is undefined whenever the catalogue is missing — `getHighnoteProducts()`
           * rejected, or the list has not arrived yet. Reading that as not-CREDIT made annual revenue
           * optional, so a CREDIT application with the field blank reported `isComplete: true`, Submit
           * enabled, and Highnote rejected it *after* the Paidol rows were written. That is exactly
           * the SPE-3659 failure `validateOnboardFormData` exists to prevent, reintroduced through
           * the back door by a failed network call.
           *
           * The trade is deliberate and asymmetric: a DEBIT applicant is asked for annual revenue
           * they did not need (annoying, self-correcting once the catalogue loads) versus a CREDIT
           * application provisioning half-way and failing (unrecoverable). Guarded on `productId`
           * because a blank one is already reported by its own `required` rule, and two errors for
           * one cause is noise.
           */
          if (!selectedProduct) return Boolean(productId);
          return selectedProduct.type === HIGHNOTE_PRODUCT_TYPE_CREDIT;
        },
        then: yup.string().required('Annual revenue is required'),
      }),
    businessIndustryClassification: yup
      .object()
      .required('Business Industry Classification is required')
      .nullable(),
    billingAddress: generateAddressSchema(poBoxMessage),
    identification: yup
      .string()
      .required('Identification is required')
      // Passes for a masked value — see `isMaskedSensitiveValue`. Note `replaceAll`, not `replace`:
      // the original stripped only the first dash, which happened not to matter for a 9-digit EIN
      // with one separator but was wrong in principle.
      .test('is-nine-digits', 'Identification must be 9 digits', (value) => {
        if (!value || isMaskedSensitiveValue(value)) return true;
        return /^\d{9}$/.test(value.replaceAll('-', ''));
      })
      // Exactly one of these two can fail: `is-nine-digits` passes for anything containing a bullet,
      // and this only fires for something containing a bullet. So the applicant sees the format
      // message or the mask message, never both, whichever actually applies.
      .test('mask-untouched', PARTIAL_MASK_MESSAGE, (value) => !isPartiallyEditedMask(value, storedMasks)),
  });
};

export const generatePrimaryAuthorizedPersonSchema = (
  poBoxMessage = DEFAULT_PO_BOX_MESSAGE,
  storedMasks: readonly string[] = []
) => {
  return yup.object().shape({
    firstName: generateFirstNameSchema(),
    middleName: generateMiddleNameSchema(),
    lastName: generateLastNameSchema(),
    suffix: generateSuffixSchema(),
    email: yup.string().email('Invalid email format').required('Email is required'),
    phoneNumber: generatePhoneNumberSchema(storedMasks),
    phoneNumberValue: generatePhoneNumberValueSchema(storedMasks),
    hasExtension: yup.boolean().nullable(),
    phoneExtension: yup
      .string()
      .when('hasExtension', {
        is: true,
        then: yup.string().required('You must enter an extension'),
      })
      .nullable(),
    birthdate: generateBirthdateSchema(storedMasks),
    homeAddress: generateAddressSchema(poBoxMessage),
    socialSecurityNumber: generateSSNSchema(storedMasks),
    jobTitle: yup.string().required('Job title is required'),
    percentageOwnership: yup
      .number()
      .required('Percentage ownership is required')
      .integer('Percentage ownership must be a whole number')
      .min(0, 'Percentage ownership cannot be negative')
      .max(100, 'Percentage ownership cannot exceed 100')
      .typeError("Please enter a valid number, if you don't have a percentage fill in 0."),
    primaryAuthorizedPersonConsent: yup.object().shape({
      cardholderAgreementPermission: yup
        .boolean()
        .required('Cardholder Agreement permission is required')
        .oneOf([true], 'Cardholder Agreement permission is required'),
      issuingPermission: yup
        .boolean()
        .required('Issuing permission is required')
        .oneOf([true], 'Issuing permission is required'),
      /**
       * Translated, unlike its neighbours in this file, and reworded.
       *
       * "Primary Authorized Person signature is required" names an internal role at someone who
       * only knows they are filling in a form, and it was the one message an applicant could summon
       * by pressing a button — Clear used to validate on the spot. Both halves were reported during
       * security testing on 2026-08-01.
       *
       * The rest of this file is still English literals. That is a known gap, deliberately scoped
       * out of the i18n pass, which covered the session refusal copy; this one is translated because
       * it is the one that was reported, not because the convention has changed.
       */
      signature: yup.string().nullable().required(translateOrKey('onboardingSignatureRequired')),
    }),
  });
};

export const generateAdministratorSchema = () => {
  return yup.object().shape({
    firstName: generateFirstNameSchema(),
    lastName: generateLastNameSchema(),
    email: yup.string().email('Invalid email format').required('Email is required'),
  });
};

interface TestContext extends yup.TestContext {
  from: {
    value: {
      beneficialOwners: CompleteBeneficialOwner[];
    };
  }[];
}

/**
 * `storedMasks` covers every owner on the application rather than just the one being validated: the
 * schema is reused across an array, so an index-keyed lookup would have to guess yup's array path
 * format. Membership is the check that matters — a value containing a bullet has to be one the server
 * sent. An owner pasting another owner's mask still gets refused, just server-side.
 */
export const generateBeneficialOwnerSchema = (
  poBoxMessage = DEFAULT_PO_BOX_MESSAGE,
  storedMasks: readonly string[] = []
) => {
  return yup.object().shape({
    firstName: generateFirstNameSchema(),
    middleName: generateMiddleNameSchema(),
    lastName: generateLastNameSchema(),
    suffix: generateSuffixSchema(),
    email: yup.string().email('Invalid email format').required('Email is required'),
    phoneNumber: generatePhoneNumberSchema(storedMasks),
    phoneNumberValue: generatePhoneNumberValueSchema(storedMasks),
    hasExtension: yup.boolean().nullable(),
    phoneExtension: yup
      .string()
      .when('hasExtension', {
        is: true,
        then: yup.string().required('You must enter an extension'),
      })
      .nullable(),
    homeAddress: generateAddressSchema(poBoxMessage),
    socialSecurityNumber: generateSSNSchema(storedMasks),
    birthdate: generateBirthdateSchema(storedMasks),
    percentageOwnership: yup
      .number()
      .required('Percentage ownership is required')
      // SEE-1388: Highnote's `percentageOwnership` is an Int.
      .integer('Percentage ownership must be a whole number')
      .min(25, 'Percentage ownership must be at least 25')
      .max(100, 'Percentage ownership cannot exceed 100')
      .test('sumOfPercentages', 'The sum of percentages cannot exceed 100%', (value, context) => {
        const realContext = context as TestContext;
        const [_, parentSchema] = realContext.from;
        const sumOfPercentages = parentSchema.value.beneficialOwners.reduce(
          (acc, owner) => acc + Number(owner.percentageOwnership ?? 0),
          0
        );
        return sumOfPercentages <= 100;
      })
      .typeError('Please enter a valid number'),
  });
};

/**
 * Sections of a persisted onboarding application that carry Highnote-required fields.
 * `administrator` is intentionally absent — it is not part of the Highnote account-holder payload.
 */
export type OnboardFormDataSection = 'organizationProfile' | 'primaryAuthorizedPerson' | 'beneficialOwners';

export type OnboardFormDataValidation = {
  isComplete: boolean;
  incompleteSections: OnboardFormDataSection[];
};

type ValidateOnboardFormDataOptions = {
  products?: (OnboardingProduct | null)[] | null;
  poBoxMessage?: string;
};

/**
 * `beneficialOwners[].percentageOwnership` has a `sumOfPercentages` test that reads its
 * grandparent value, so an owner can only be validated inside the same wrapper shape the
 * beneficial-owners step uses.
 */
const isSectionValid = (schema: yup.AnySchema, value: unknown, context: Record<string, unknown>) => {
  try {
    return schema.isValidSync(value, { context });
  } catch {
    // Malformed persisted data (e.g. a non-array `beneficialOwners`) can make a Yup test throw.
    // Treat it as incomplete rather than letting it reach Highnote.
    return false;
  }
};

/**
 * Every mask anywhere inside `value`, however deeply nested.
 *
 * Prefer this over enumerating fields by hand with `collectStoredMasks`. A hand-written list has to
 * be kept in step with the server's `SENSITIVE_FIELDS` registry, and getting it wrong fails in the
 * least visible way there is: the mask the form forgot to collect reads as a *partially edited* one,
 * so Next is permanently unclickable on every resumed application, with an error naming a field the
 * applicant never touched. That already happened once, with the business phone.
 *
 * Structurally immune instead: it walks whatever it is given, so a field added server-side is
 * collected without a frontend change.
 */
export const collectMasksDeep = (value: unknown): string[] => {
  if (typeof value === 'string') return isMaskedSensitiveValue(value) ? [value] : [];
  if (Array.isArray(value)) return value.flatMap(collectMasksDeep);
  if (value && typeof value === 'object') return Object.values(value).flatMap(collectMasksDeep);
  return [];
};

/**
 * SPE-3659: validates a persisted `Onboard.formData` against the same schemas the individual
 * steps use, so the final submit cannot send an application that Highnote will reject for a
 * missing required field. Adds no rules of its own.
 *
 * SEE-1274: the masks in that persisted data are, by definition, ones the server sent — a partially
 * edited value could never have been stored, because the save that carried it would have been
 * rejected. So the schemas' partial-edit rule has nothing to catch here and every mask present is
 * supplied as known-good. Without this, the section schemas treated every masked value as an edited
 * one and the final step declared every resumed application incomplete.
 */
export const validateOnboardFormData = (
  formData: unknown,
  { products, poBoxMessage = DEFAULT_PO_BOX_MESSAGE }: ValidateOnboardFormDataOptions = {}
): OnboardFormDataValidation => {
  const sections = (formData ?? {}) as Record<OnboardFormDataSection, unknown>;
  const storedMasks = collectMasksDeep(formData);
  // `generateOrganizationProfileSchema` dereferences every element of `$products`, so drop any
  // nulls a caller passes through.
  const context = {
    products: (products ?? []).filter((product): product is OnboardingProduct => !!product),
  };
  const incompleteSections: OnboardFormDataSection[] = [];

  if (
    !isSectionValid(
      generateOrganizationProfileSchema(poBoxMessage, storedMasks),
      sections.organizationProfile,
      context
    )
  ) {
    incompleteSections.push('organizationProfile');
  }

  if (
    !isSectionValid(
      generatePrimaryAuthorizedPersonSchema(poBoxMessage, storedMasks),
      sections.primaryAuthorizedPerson,
      context
    )
  ) {
    incompleteSections.push('primaryAuthorizedPerson');
  }

  const beneficialOwnersSchema = yup.object().shape({
    beneficialOwners: yup.array().of(generateBeneficialOwnerSchema(poBoxMessage, storedMasks)).required(),
  });
  if (
    !isSectionValid(beneficialOwnersSchema, { beneficialOwners: sections.beneficialOwners ?? [] }, context)
  ) {
    incompleteSections.push('beneficialOwners');
  }

  return { isComplete: incompleteSections.length === 0, incompleteSections };
};

function getFakeAddress() {
  return {
    country: 'USA',
    state: faker.location.state({ abbreviated: true }),
    city: faker.location.city(),
    address: faker.location.streetAddress(),
    extendedAddress: '',
    zip: faker.location.zipCode(),
  };
}

function generatePhoneNumberValueSchema(storedMasks: readonly string[] = []) {
  return yup
    .string()
    .required('Phone number is required')
    .test('custom', 'Invalid phone number', (value) => {
      // `matchIsValidTel` cannot pass a mask, and it is not supposed to — the real number is
      // server-side. The mask test below is what polices this field while it holds one.
      if (isMaskedSensitiveValue(value)) return true;
      return matchIsValidTel(value || '');
    })
    .test('mask-untouched', PARTIAL_MASK_MESSAGE, (value) => !isPartiallyEditedMask(value, storedMasks));
}

function generateSSNSchema(storedMasks: readonly string[] = []) {
  return (
    yup
      .string()
      .required('Social security number is required')
      // See `isPartiallyEditedMask`. Mutually exclusive with the two format tests below, which both
      // pass for any value containing a bullet.
      .test('mask-untouched', PARTIAL_MASK_MESSAGE, (value) => !isPartiallyEditedMask(value, storedMasks))
      // Both checks pass for a masked value and for an empty one: a mask means "keep what is stored",
      // and `.required()` owns emptiness (same convention as the birthdate test above). Validating a
      // mask against a digit format can never succeed — the real value is checked server-side.
      .test('is-nine-digits', 'Social Security Number must be 9 digits', (value) => {
        if (!value || isMaskedSensitiveValue(value)) return true;
        return /^\d{9}$/.test(value.replaceAll('-', ''));
      })
      .test('ssn-pattern', 'Invalid Social Security Number', (value) => {
        if (!value || isMaskedSensitiveValue(value)) return true;
        return /^(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/.test(value);
      })
  );
}

function generateLastNameSchema() {
  return yup
    .string()
    .required('Last name is required')
    .matches(/^[A-Za-z',.\s-]{2,255}$/, 'Invalid last name');
}

function generateFirstNameSchema() {
  return (
    yup
      .string()
      .required('First name is required')
      // SEE-1388: Highnote takes a one-letter given name, so 2 was refusing real applicants.
      .matches(/^[A-Za-z',.\s-]{1,255}$/, 'Invalid first name')
  );
}

function generateMiddleNameSchema() {
  return yup
    .string()
    .matches(/^[A-Za-z',.\s-]{0,255}$/, 'Invalid middle name')
    .nullable();
}

function generateSuffixSchema() {
  return yup
    .string()
    .matches(/^[\w',.\s-]{0,255}$/, 'Invalid suffix')
    .nullable();
}
